import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DocumentUploadForm = ({ formData, errors, onFileChange }) => {
  const [licensePreview, setLicensePreview] = useState(null);
  const [affiliationPreview, setAffiliationPreview] = useState(null);
  
  const licenseInputRef = useRef(null);
  const affiliationInputRef = useRef(null);

  const handleFilePreview = (file, setPreview) => {
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleLicenseChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      onFileChange('licenseDocument', file);
      handleFilePreview(file, setLicensePreview);
    }
  };

  const handleAffiliationChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      onFileChange('affiliationDocument', file);
      handleFilePreview(file, setAffiliationPreview);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Upload" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">Document Upload</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">Upload required verification documents</p>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          <Icon name="Info" size={16} className="sm:w-[18px] sm:h-[18px] flex-shrink-0 mt-0.5" color="var(--color-primary)" />
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-foreground font-medium mb-1">Document Requirements</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li className="flex items-start gap-1.5">
                <span className="text-primary mt-0.5">•</span>
                <span>PDF, JPG, or PNG format</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-primary mt-0.5">•</span>
                <span>Max file size: 5MB</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-primary mt-0.5">•</span>
                <span>Clear, legible images</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Medical License Document <span className="text-error">*</span>
          </label>
          <div 
            className="border-2 border-dashed border-border rounded-lg p-6 hover:border-primary transition-clinical cursor-pointer"
            onClick={() => licenseInputRef?.current?.click()}
          >
            {licensePreview ? (
              <div className="space-y-3">
                <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                  <Image
                    src={licensePreview}
                    alt="Medical license document preview showing uploaded verification file"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground truncate">
                    {formData?.licenseDocument?.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setLicensePreview(null);
                      onFileChange('licenseDocument', null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-muted-foreground">PDF, JPG, PNG up to 5MB</p>
              </div>
            )}
          </div>
          <input
            ref={licenseInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleLicenseChange}
            className="hidden"
          />
          {errors?.licenseDocument && (
            <p className="text-sm text-error mt-2">{errors?.licenseDocument}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Institutional Affiliation Proof <span className="text-error">*</span>
          </label>
          <div 
            className="border-2 border-dashed border-border rounded-lg p-6 hover:border-primary transition-clinical cursor-pointer"
            onClick={() => affiliationInputRef?.current?.click()}
          >
            {affiliationPreview ? (
              <div className="space-y-3">
                <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                  <Image
                    src={affiliationPreview}
                    alt="Institutional affiliation document preview showing uploaded verification file"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground truncate">
                    {formData?.affiliationDocument?.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setAffiliationPreview(null);
                      onFileChange('affiliationDocument', null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-muted-foreground">PDF, JPG, PNG up to 5MB</p>
              </div>
            )}
          </div>
          <input
            ref={affiliationInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleAffiliationChange}
            className="hidden"
          />
          {errors?.affiliationDocument && (
            <p className="text-sm text-error mt-2">{errors?.affiliationDocument}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadForm;